import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  FaBookOpen,
  FaBullseye,
  FaCalendarAlt,
  FaChalkboardTeacher,
  FaEdit,
  FaFileExcel,
  FaFilePdf,
  FaFilter,
  FaInfoCircle,
  FaLayerGroup,
  FaPlus,
  FaSave,
  FaSearch,
  FaTimes,
  FaTrash,
  FaUsers,
} from 'react-icons/fa';
import YoungApprenticeService from '../services/YoungApprenticeService';
import { downloadLessonPlanPdf, downloadLessonPlanXlsx } from './lessonPlanReports';
import './LessonPlanPanel.css';

const topicId = () => globalThis.crypto?.randomUUID?.() || 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
  const random = Math.floor(Math.random() * 16);
  return (character === 'x' ? random : (random & 3) | 8).toString(16);
});
const emptyItem = (nucleusId = '', nucleusName = '') => ({ id: topicId(), lessonTheme: '', objective: '', nucleusId, nucleusName, plannedHours: null });
const hoursText = (value) => value == null || value === '' ? 'Não informado' : `${Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} HA`;
const blankPlan = (nucleusId = '', nucleusName = '', lessonHourMinutes = 60) => ({ lessonHourMinutes, startDate: '', endDate: '', items: [emptyItem(nucleusId, nucleusName)], classIds: [], parentIds: [] });
const blankFilters = () => ({ status: '', parentId: '', classId: '', nucleusId: '' });
const nucleusPreferenceKey = (userId) => `nash-young-apprentice-preferred-nucleus-${userId || 'current'}`;
const formatDate = (value) => value ? value.split('-').reverse().join('/') : '—';
const normalize = (value) => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
export const planItems = (plan) => {
  const fallbackNucleusId = plan?.nucleusId || '';
  const fallbackNucleusName = plan?.nucleusName || '';
  const source = plan?.items?.length ? plan.items : [{ lessonTheme: plan?.lessonTheme || '', objective: plan?.objective || '' }];
  return source.map((item) => {
    const hasTopicNucleus = Object.prototype.hasOwnProperty.call(item || {}, 'nucleusId') || Object.prototype.hasOwnProperty.call(item || {}, 'nucleusName');
    return { ...item, nucleusId: hasTopicNucleus ? (item?.nucleusId || '') : fallbackNucleusId, nucleusName: hasTopicNucleus ? (item?.nucleusName || '') : fallbackNucleusName };
  });
};

const classParentId = (item) => item?.courseId ?? item?.groupId ?? item?.parentId ?? null;
const includesId = (values, id) => Array.isArray(values) && values.some((value) => String(value) === String(id));
const getPlanStatus = (plan, today) => plan.endDate < today ? 'Concluído' : plan.startDate > today ? 'Próximo' : 'Em andamento';

export function findLessonPlans(plans, date, classId, parentId, responsibleUserId) {
  if (!date || !responsibleUserId) return [];
  return (plans || []).filter((plan) => {
    if (Number(plan.responsibleUserId) !== Number(responsibleUserId)) return false;
    if (date < plan.startDate || date > plan.endDate) return false;

    const classIds = Array.isArray(plan.classIds) ? plan.classIds : [];
    const parentIds = Array.isArray(plan.parentIds) ? plan.parentIds : [];

    // Se o plano especifica turmas, ele vale somente para elas.
    if (classIds.length) return !!classId && includesId(classIds, classId);

    // Sem turma específica, curso/grupo significa todas as turmas vinculadas a ele.
    if (parentIds.length) return !!parentId && includesId(parentIds, parentId);

    return true;
  });
}
export const findLessonPlan = (...args) => findLessonPlans(...args)[0] || null;

export function LessonPlanDialog({ children, onClose, saving = false }) {
  const titleId = useId();
  const dialog = useRef(null);
  useEffect(() => {
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      previous?.focus?.();
    };
  }, []);
  return createPortal(
    <div className="lesson-plan-dialog" onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) onClose(); }}>
      <section ref={dialog} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header>
          <div><span>Diário de Classe</span><h2 id={titleId}><FaBookOpen /> Planos de Aula</h2></div>
          <button type="button" aria-label="Fechar planos de aula" onClick={onClose} disabled={saving}><FaTimes /></button>
        </header>
        <div className="lesson-plan-dialog__body">{children}</div>
      </section>
    </div>,
    document.body,
  );
}

export default function LessonPlanPanel({
  plans = [],
  classes = [],
  parents = [],
  parentLabel = 'Cursos',
  currentUserId,
  currentUserName,
  canManage,
  saving,
  onSave,
  onDelete,
  youngApprenticeMode = false,
  lessonHourMinutes = 60,
}) {
  const [form, setForm] = useState(() => blankPlan('', '', lessonHourMinutes));
  const [view, setView] = useState('mine');
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState(blankFilters());
  const [editorParentId, setEditorParentId] = useState('');
  const [nuclei, setNuclei] = useState([]);
  const [nucleiLoading, setNucleiLoading] = useState(false);
  const [preferredNucleusId, setPreferredNucleusId] = useState(() => {
    if (!youngApprenticeMode || typeof window === 'undefined') return '';
    return window.localStorage.getItem(nucleusPreferenceKey(currentUserId)) || '';
  });

  useEffect(() => {
    setForm((current) => current.id ? current : { ...current, lessonHourMinutes });
  }, [lessonHourMinutes]);

  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    if (!youngApprenticeMode || typeof window === 'undefined') {
      setPreferredNucleusId('');
      return;
    }
    setPreferredNucleusId(window.localStorage.getItem(nucleusPreferenceKey(currentUserId)) || '');
  }, [youngApprenticeMode, currentUserId]);

  useEffect(() => {
    if (!youngApprenticeMode) {
      setNuclei([]);
      return undefined;
    }
    let active = true;
    setNucleiLoading(true);
    YoungApprenticeService.listLookup('nucleus', { includeInactive: false })
      .then((rows) => { if (active) setNuclei(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (active) setNuclei([]); })
      .finally(() => { if (active) setNucleiLoading(false); });
    return () => { active = false; };
  }, [youngApprenticeMode]);

  useEffect(() => {
    if (!youngApprenticeMode || form.id || !preferredNucleusId) return;
    const preferred = nuclei.find((item) => String(item.id) === String(preferredNucleusId));
    if (!preferred) return;
    setForm((current) => {
      const first = current.items?.[0];
      if (!first || first.nucleusId || first.lessonTheme || first.objective) return current;
      return { ...current, items: current.items.map((item, index) => index === 0 ? { ...item, nucleusId: preferred.id, nucleusName: preferred.name || '' } : item) };
    });
  }, [youngApprenticeMode, preferredNucleusId, nuclei, form.id]);

  const selectedClasses = useMemo(() => new Set((form.classIds || []).map(String)), [form.classIds]);
  const selectedParents = useMemo(() => new Set((form.parentIds || []).map(String)), [form.parentIds]);
  const mine = (plan) => Number(plan.responsibleUserId) === Number(currentUserId);
  const minePlans = useMemo(() => plans.filter((plan) => Number(plan.responsibleUserId) === Number(currentUserId)), [plans, currentUserId]);
  const mineTopicCount = useMemo(() => minePlans.reduce((total, plan) => total + planItems(plan).length, 0), [minePlans]);

  const classById = useMemo(() => new Map(classes.map((item) => [String(item.id), item])), [classes]);
  const parentById = useMemo(() => new Map(parents.map((item) => [String(item.id), item])), [parents]);
  const activeParents = useMemo(() => parents.filter((item) => item.status === 'ACTIVE'), [parents]);
  const activeClasses = useMemo(() => {
    const activeParentIds = new Set(activeParents.map((item) => String(item.id)));
    return classes.filter((item) => item.status === 'ACTIVE' && activeParentIds.has(String(classParentId(item))));
  }, [classes, activeParents]);
  const availableEditorClasses = useMemo(
    () => editorParentId ? activeClasses.filter((item) => String(classParentId(item) || '') === String(editorParentId)) : [],
    [activeClasses, editorParentId],
  );
  const availableFilterClasses = useMemo(
    () => filters.parentId
      ? activeClasses.filter((item) => String(classParentId(item) || '') === String(filters.parentId))
      : activeClasses,
    [activeClasses, filters.parentId],
  );

  const coverage = (plan) => {
    const classNames = (plan.classIds || []).map((id) => classById.get(String(id))?.name).filter(Boolean);
    const parentNames = (plan.parentIds || []).map((id) => parentById.get(String(id))?.name).filter(Boolean);
    if (classNames.length) {
      return {
        compact: classNames.join(' · '),
        detail: `Turmas: ${classNames.join(', ')}${parentNames.length ? ` • ${parentLabel}: ${parentNames.join(', ')}` : ''}`,
      };
    }
    if (parentNames.length) {
      return {
        compact: `Todas as turmas de ${parentNames.join(' · ')}`,
        detail: `Todas as turmas vinculadas a ${parentLabel.toLowerCase()}: ${parentNames.join(', ')}`,
      };
    }
    return { compact: 'Todas as turmas', detail: 'Todas as turmas' };
  };

  const visiblePlans = useMemo(() => plans.filter((plan) => {
    if (view === 'mine' && Number(plan.responsibleUserId) !== Number(currentUserId)) return false;

    const status = getPlanStatus(plan, today);
    if (filters.status && status !== filters.status) return false;
    if (youngApprenticeMode && filters.nucleusId && !planItems(plan).some((item) => String(item.nucleusId || '') === String(filters.nucleusId))) return false;

    if (filters.parentId) {
      const directParent = includesId(plan.parentIds, filters.parentId);
      const parentFromSpecificClass = (plan.classIds || []).some((id) => String(classParentId(classById.get(String(id))) || '') === String(filters.parentId));
      if (!directParent && !parentFromSpecificClass) return false;
    }

    if (filters.classId) {
      const selectedClass = classById.get(String(filters.classId));
      const selectedParentId = classParentId(selectedClass);
      const specificClasses = Array.isArray(plan.classIds) ? plan.classIds : [];
      const parentScope = Array.isArray(plan.parentIds) ? plan.parentIds : [];
      const classMatches = specificClasses.length
        ? includesId(specificClasses, filters.classId)
        : (parentScope.length ? includesId(parentScope, selectedParentId) : true);
      if (!classMatches) return false;
    }

    const audience = coverage(plan);
    const searchText = [
      planItems(plan).map((item) => `${item.lessonTheme} ${item.objective} ${item.nucleusName || ''}`).join(' '),
      plan.responsibleName,
      audience.compact,
      audience.detail,
      status,
    ].join(' ');
    return normalize(searchText).includes(normalize(query));
  }), [plans, view, currentUserId, today, filters, query, classById, parentById, youngApprenticeMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (field, id) => setForm((current) => ({
    ...current,
    [field]: current[field].map(String).includes(String(id))
      ? current[field].filter((value) => String(value) !== String(id))
      : [...current[field], Number(id)],
  }));
  const changeItem = (index, field, value) => setForm((current) => ({
    ...current,
    items: current.items.map((item, position) => position === index ? { ...item, [field]: value } : item),
  }));
  const removeItem = (index) => setForm((current) => ({
    ...current,
    items: current.items.length === 1 ? current.items : current.items.filter((_, position) => position !== index),
  }));
  const submit = async (event) => {
    event.preventDefault();
    const first = form.items[0] || {};
    const items = form.items.map((item) => ({ ...item, plannedHours: item.plannedHours === '' || item.plannedHours == null ? null : Number(item.plannedHours) }));
    const ok = await onSave({ ...form, items, lessonTheme: first.lessonTheme, objective: first.objective });
    if (ok) {
      const preferred = youngApprenticeMode ? nuclei.find((item) => String(item.id) === String(preferredNucleusId)) : null;
      setForm(blankPlan(preferred?.id || '', preferred?.name || '', lessonHourMinutes));
    }
  };
  const edit = (plan) => {
    if (!mine(plan)) return;
    const firstParentId = plan.parentIds?.[0]
      || classParentId(classes.find((item) => includesId(plan.classIds || [], item.id)))
      || '';
    setEditorParentId(String(firstParentId || ''));
    setForm({ ...plan, items: planItems(plan).map((item) => ({ ...item, id: item.id || topicId() })), parentIds: plan.parentIds || [] });
    document.querySelector('.lesson-plan-dialog__body')?.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const clearFilters = () => {
    setQuery('');
    setFilters(blankFilters());
  };
  const reportArgs = { plans: visiblePlans, classes, parents, parentLabel, includeNucleus: youngApprenticeMode, nucleusId: youngApprenticeMode ? filters.nucleusId : '' };
  const hasActiveFilters = !!query || !!filters.status || !!filters.parentId || !!filters.classId || (youngApprenticeMode && !!filters.nucleusId);
  const setItemNucleus = (index, value) => {
    const nucleus = nuclei.find((item) => String(item.id) === String(value));
    setForm((current) => ({
      ...current,
      items: current.items.map((item, position) => position === index
        ? { ...item, nucleusId: nucleus?.id || '', nucleusName: nucleus?.name || '' }
        : item),
    }));
  };
  const saveNucleusPreference = (item) => {
    if (!youngApprenticeMode || typeof window === 'undefined') return;
    const value = String(item?.nucleusId || '');
    if (value) {
      window.localStorage.setItem(nucleusPreferenceKey(currentUserId), value);
      setPreferredNucleusId(value);
    } else {
      window.localStorage.removeItem(nucleusPreferenceKey(currentUserId));
      setPreferredNucleusId('');
    }
  };
  const preferredNucleusName = nuclei.find((item) => String(item.id) === String(preferredNucleusId))?.name || '';
  const scopeText = form.classIds?.length
    ? `${form.classIds.length} turma(s) específica(s)`
    : form.parentIds?.length
      ? `Todas as turmas de ${form.parentIds.length} ${parentLabel.toLowerCase()} selecionado(s)`
      : 'Selecione ao menos uma turma ou curso/grupo';

  return <div className="lesson-plan">
    <section className="lesson-plan__intro">
      <div className="lesson-plan__intro-icon"><FaBookOpen /></div>
      <div><span>Planejamento pedagógico</span><h3>Planos organizados por período, turma e tema</h3></div>
      <div className="lesson-plan__stats">
        <div><strong>{minePlans.length}</strong><small>planos</small></div>
        <div><strong>{mineTopicCount}</strong><small>temas</small></div>
      </div>
    </section>

    {canManage && <section className="lesson-plan__card lesson-plan__editor">
      <header>
        <div><span className="lesson-plan__step">1</span><div><h3>{form.id ? 'Editar plano' : 'Novo plano de aula'}</h3><small>Defina o período, os temas e onde o plano será aplicado.</small></div></div>
        {form.id && <button type="button" className="lesson-plan__secondary" onClick={() => { const preferred = nuclei.find((item) => String(item.id) === String(preferredNucleusId)); setEditorParentId(''); setForm(blankPlan(preferred?.id || '', preferred?.name || '', lessonHourMinutes)); }}><FaPlus /> Novo</button>}
      </header>

      <form onSubmit={submit}>
        <div className="lesson-plan__grid">
          <label><span><FaCalendarAlt /> Início *</span><input required type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} /></label>
          <label><span><FaCalendarAlt /> Fim *</span><input required type="date" min={form.startDate} value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} /></label>
        </div>

        <div className="lesson-plan__topics">
          <div className="lesson-plan__topics-heading">
            <div><strong>Temas, objetivos e núcleo</strong><small>{form.items.length} tema(s) neste período{youngApprenticeMode && preferredNucleusName ? ` • núcleo padrão: ${preferredNucleusName}` : ''}</small></div>
            <button type="button" onClick={() => { const preferred = nuclei.find((item) => String(item.id) === String(preferredNucleusId)); setForm({ ...form, items: [...form.items, emptyItem(preferred?.id || '', preferred?.name || '')] }); }}><FaPlus /> Adicionar tema</button>
          </div>
          {form.items.map((item, index) => <article key={item.id} className="lesson-plan__topic-row">
            <span className="lesson-plan__topic-number">{index + 1}</span>
            {youngApprenticeMode && <label className="lesson-plan__topic-nucleus"><span><FaLayerGroup /> Núcleo <small>opcional</small></span><div><select value={item.nucleusId || ''} disabled={nucleiLoading} onChange={(e) => setItemNucleus(index, e.target.value)}><option value="">{nucleiLoading ? 'Carregando...' : 'Sem núcleo'}</option>{nuclei.map((nucleus) => <option key={nucleus.id} value={nucleus.id}>{nucleus.name}</option>)}</select><button type="button" className={String(item.nucleusId || '') === String(preferredNucleusId || '') && item.nucleusId ? 'is-preferred' : ''} title={item.nucleusId ? 'Salvar este núcleo como padrão' : 'Limpar preferência de núcleo'} onClick={() => saveNucleusPreference(item)}><FaSave /></button></div></label>}
            <label className="lesson-plan__topic-theme"><span><FaBookOpen /> Tema da aula *</span><input required maxLength={180} placeholder="Digite o tema" value={item.lessonTheme} onChange={(e) => changeItem(index, 'lessonTheme', e.target.value)} /></label>
            <label className="lesson-plan__topic-objective"><span><FaBullseye /> Objetivo *</span><input required maxLength={180} placeholder="Digite o objetivo" value={item.objective} onChange={(e) => changeItem(index, 'objective', e.target.value)} /></label>
            <label className="lesson-plan__topic-hours"><span>HA previstas <span className="lesson-plan__info" tabIndex={0} aria-label="Sobre horas-aula"><FaInfoCircle /><span role="tooltip">{form.lessonHourMinutes == null ? 'Unidade de hora-aula não informada neste plano antigo.' : `Uma hora-aula deste plano equivale a ${form.lessonHourMinutes} minutos.`} O realizado vem dos registros do diário.</span></span></span><input type="number" min="0" max="10000" step="0.01" placeholder="Opcional" value={item.plannedHours ?? ''} onChange={(e) => changeItem(index, 'plannedHours', e.target.value)} /></label>
            <button type="button" className="lesson-plan__remove-topic" aria-label={`Remover tema ${index + 1}`} disabled={form.items.length === 1} onClick={() => removeItem(index)}><FaTrash /></button>
          </article>)}
        </div>

        <div className="lesson-plan__targets">
          <fieldset>
            <legend><FaBookOpen /> {parentLabel}</legend>
            <label className="lesson-plan__target-parent">
              <span>Selecione primeiro {parentLabel.toLowerCase()}</span>
              <select value={editorParentId} onChange={(event) => setEditorParentId(event.target.value)}>
                <option value="">Selecione</option>
                {activeParents.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
            </label>
            {activeParents.some((item) => String(item.id) === String(editorParentId)) && <label><input type="checkbox" checked={selectedParents.has(String(editorParentId))} onChange={() => toggle('parentIds', editorParentId)} /><span>Aplicar a todas as turmas deste {parentLabel.toLowerCase().replace(/s$/, '')}</span></label>}
          </fieldset>
          <fieldset>
            <legend><FaUsers /> Turmas</legend>
            {!editorParentId ? <p>Selecione {parentLabel.toLowerCase()} para visualizar as turmas.</p> : <div>{availableEditorClasses.map((item) => <label key={item.id}><input type="checkbox" checked={selectedClasses.has(String(item.id))} onChange={() => toggle('classIds', item.id)} /><span>{item.name}</span></label>)}</div>}
          </fieldset>
        </div>

        <div className={`lesson-plan__scope ${form.classIds?.length || form.parentIds?.length ? 'is-ready' : ''}`}>
          <FaUsers />
          <span>{scopeText}</span>
        </div>

        <footer>
          <span>Responsável: <strong>{currentUserName || 'Você'}</strong></span>
          <button className="lesson-plan__primary" type="submit" disabled={saving}><FaSave />{saving ? 'Salvando...' : form.id ? 'Salvar alterações' : 'Registrar plano'}</button>
        </footer>
      </form>
    </section>}

    <section className="lesson-plan__card lesson-plan__library">
      <header>
        <div><span className="lesson-plan__step">2</span><div><h3>Consulta de planos de aula</h3><small>Filtre, consulte e gere relatórios dos planos cadastrados.</small></div></div>
        <div className="lesson-plan__reports">
          <button type="button" disabled={!visiblePlans.length} onClick={() => downloadLessonPlanPdf(reportArgs)}><FaFilePdf /> PDF</button>
          <button type="button" disabled={!visiblePlans.length} onClick={() => downloadLessonPlanXlsx(reportArgs)}><FaFileExcel /> Excel</button>
        </div>
      </header>

      <div className="lesson-plan__query-panel">
        <div className="lesson-plan__query-top">
          <div className="lesson-plan__switch">
            <button type="button" className={view === 'mine' ? 'active' : ''} onClick={() => setView('mine')}>Meus planos</button>
            <button type="button" className={view === 'all' ? 'active' : ''} onClick={() => setView('all')}>Toda a equipe</button>
          </div>
          <label className="lesson-plan__search"><FaSearch /><input type="search" aria-label="Buscar planos" placeholder="Buscar tema, objetivo, turma ou responsável" value={query} onChange={(e) => setQuery(e.target.value)} /></label>
        </div>

        <div className="lesson-plan__filters">
          <label><span><FaFilter /> Status</span><select value={filters.status} onChange={(e) => setFilters((current) => ({ ...current, status: e.target.value }))}><option value="">Todos</option><option value="Em andamento">Em andamento</option><option value="Próximo">Próximos</option><option value="Concluído">Concluídos</option></select></label>
          <label><span><FaBookOpen /> {parentLabel}</span><select value={filters.parentId} onChange={(e) => setFilters((current) => ({ ...current, parentId: e.target.value, classId: '' }))}><option value="">Todos</option>{activeParents.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
          <label><span><FaUsers /> Turma</span><select value={filters.classId} onChange={(e) => setFilters((current) => ({ ...current, classId: e.target.value }))}><option value="">Todas</option>{availableFilterClasses.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
          {youngApprenticeMode && <label><span><FaLayerGroup /> Núcleo</span><select value={filters.nucleusId} onChange={(e) => setFilters((current) => ({ ...current, nucleusId: e.target.value }))}><option value="">Todos</option>{nuclei.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>}
          <div className="lesson-plan__filter-summary"><strong>{visiblePlans.length}</strong><span>plano(s) encontrado(s)</span>{hasActiveFilters && <button type="button" onClick={clearFilters}><FaTimes /> Limpar filtros</button>}</div>
        </div>
      </div>

      {!visiblePlans.length ? <div className="lesson-plan__empty"><FaBookOpen /><strong>Nenhum plano encontrado</strong><span>Ajuste os filtros ou cadastre um novo plano.</span></div> : <div className="lesson-plan__list">
        {visiblePlans.map((plan) => {
          const owned = mine(plan);
          const state = getPlanStatus(plan, today);
          const audience = coverage(plan);
          const visibleTopics = planItems(plan).filter((item) => !youngApprenticeMode || !filters.nucleusId || String(item.nucleusId || '') === String(filters.nucleusId));
          return <article key={plan.id}>
            <div className="lesson-plan__card-top"><span className={`lesson-plan__status lesson-plan__status--${state === 'Em andamento' ? 'current' : state === 'Próximo' ? 'next' : 'done'}`}>{state}</span><span className="lesson-plan__period"><FaCalendarAlt /> {formatDate(plan.startDate)} — {formatDate(plan.endDate)}</span></div>
            <div className="lesson-plan__topic-list">{visibleTopics.map((item, index) => <div key={item.id || `${item.lessonTheme}-${item.objective}`}><span className="lesson-plan__topic-index">{index + 1}</span><div><strong>{item.lessonTheme}</strong><span><FaBullseye /> {item.objective}</span><span className="lesson-plan__hours">Previsto: {hoursText(item.plannedHours)} · Realizado: {hoursText(item.actualLessonHours)}{item.actualMinutes != null ? ` (${item.actualMinutes} min)` : ''}</span>{Number(item.sessionsWithoutDuration) > 0 && <small>{item.sessionsWithoutDuration} registro(s) sem duração</small>}{youngApprenticeMode && item.nucleusName && <small className="lesson-plan__topic-nucleus-badge"><FaLayerGroup /> {item.nucleusName}</small>}</div></div>)}</div>
            {Number(plan.timeSummary?.unlinkedSessions) > 0 && <p className="lesson-plan__unlinked-warning" role="status">{plan.timeSummary.unlinkedSessions} registros do diário sem vínculo com tema; não incluídos no realizado.</p>}
            {plan.timeSummary && <div className="lesson-plan__time-summary"><strong>Total do plano: {hoursText(plan.timeSummary.plannedHours)} previsto · {hoursText(plan.timeSummary.actualLessonHours)} realizado</strong><small>{plan.lessonHourMinutes == null ? 'Unidade de HA não informada' : `1 HA = ${plan.lessonHourMinutes} min`}{Number(plan.timeSummary.sessionsWithoutDuration) > 0 ? ` · ${plan.timeSummary.sessionsWithoutDuration} registro(s) sem duração` : ''}</small>{Number(plan.timeSummary.topicsWithoutPlannedHours) > 0 && <small>{plan.timeSummary.topicsWithoutPlannedHours} tema(s) sem previsão</small>}{(plan.timeSummary.orphanTopics || []).map((topic) => <small key={topic.id}>Tema removido: {topic.lessonTheme} · {topic.actualMinutes} min realizados{Number(topic.sessionsWithoutDuration) > 0 ? ` · ${topic.sessionsWithoutDuration} registro(s) sem duração` : ''}</small>)}</div>}
            <div className="lesson-plan__audience"><FaUsers /><div><strong>{audience.compact}</strong><small>{audience.detail !== audience.compact ? audience.detail : ''}</small></div></div>
            <footer><span><FaChalkboardTeacher /> {plan.responsibleName || (owned ? currentUserName : '') || 'Responsável não identificado'}</span>{owned && canManage && <aside><button type="button" title="Editar meu plano" onClick={() => edit(plan)}><FaEdit /> Editar</button><button type="button" className="danger" title="Excluir meu plano" onClick={() => onDelete(plan.id)}><FaTrash /></button></aside>}</footer>
          </article>;
        })}
      </div>}
    </section>
  </div>;
}
